import CryptoJS from "crypto-js";
import { AES_SECRET_KEY } from "configs/auth.config";
import { getCompanyIdFromToken, getUserIdFromToken } from "./tokenUtils";

/**
 * Encrypts permissions string and stores in localStorage
 * @param {string} permissionsString - Permissions string (e.g., "BBSDW | BUWEFNW | MOWFO")
 * @returns {void}
 */
export const storeEncryptedPermissions = (permissionsString) => {
  if (!permissionsString || typeof permissionsString !== "string") {
    console.warn("Invalid permissions string provided");
    return;
  }

  try {
    const encrypted = CryptoJS.AES.encrypt(permissionsString, AES_SECRET_KEY).toString();
    localStorage.setItem("encryptedPermissions", encrypted);
  } catch (err) {
    console.error("Failed to encrypt and store permissions:", err);
  }
};

/**
 * Retrieves and decrypts permissions from localStorage
 * @returns {string|null} - Decrypted permissions string or null if not found
 */
export const getDecryptedPermissions = () => {
  try {
    const encrypted = localStorage.getItem("encryptedPermissions");
    
    if (!encrypted) {
      return null;
    }

    const decrypted = CryptoJS.AES.decrypt(encrypted, AES_SECRET_KEY).toString(CryptoJS.enc.Utf8);
    return decrypted || null;
  } catch (err) {
    console.error("Failed to decrypt permissions:", err);
    return null;
  }
};

/**
 * Parses permissions string into an array of permission codes
 * Handles format: "BBSDW | BUWEFNW | MOWFO"
 * @param {string} permissionsString - Permissions string
 * @returns {string[]} - Array of permission codes
 */
export const parsePermissions = (permissionsString) => {
  if (!permissionsString || typeof permissionsString !== "string") {
    return [];
  }

  return permissionsString
    .split("|")
    .map((perm) => perm.trim())
    .filter((perm) => perm.length > 0);
};

/**
 * Checks if a specific permission exists in the permissions string.
 * Pass an array to check instead whether ANY of those codes is present
 * (useful for a menu item/route that should be reachable via either of two permissions).
 * @param {string|string[]} permissionCode - Permission code to check (e.g., "BBSDW"), or an array of codes (ANY match)
 * @returns {boolean} - True if permission exists, false otherwise
 */
export const hasPermission = (permissionCode) => {
  try {
    if (Array.isArray(permissionCode)) {
      return permissionCode.some((code) => hasPermission(code));
    }

    const permissionsString = getDecryptedPermissions();

    if (!permissionsString) {
      return false;
    }

    const permissions = parsePermissions(permissionsString);
    return permissions.includes(permissionCode);
  } catch (err) {
    console.error("Failed to check permission:", err);
    return false;
  }
};

/**
 * Checks if user has any of the provided permissions
 * @param {string[]} permissionCodes - Array of permission codes to check
 * @returns {boolean} - True if user has at least one of the permissions
 */
export const hasAnyPermission = (permissionCodes) => {
  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
    return false;
  }

  return permissionCodes.some((code) => hasPermission(code));
};

/**
 * Checks if user has all of the provided permissions
 * @param {string[]} permissionCodes - Array of permission codes to check
 * @returns {boolean} - True if user has all of the permissions
 */
export const hasAllPermissions = (permissionCodes) => {
  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
    return true;
  }

  return permissionCodes.every((code) => hasPermission(code));
};

/**
 * Gets all permissions as an array
 * @returns {string[]} - Array of all permission codes
 */
export const getAllPermissions = () => {
  try {
    const permissionsString = getDecryptedPermissions();
    return permissionsString ? parsePermissions(permissionsString) : [];
  } catch (err) {
    console.error("Failed to get all permissions:", err);
    return [];
  }
};

/**
 * Clears encrypted permissions from localStorage
 * @returns {void}
 */
export const clearPermissions = () => {
  try {
    localStorage.removeItem("encryptedPermissions");
  } catch (err) {
    console.error("Failed to clear permissions:", err);
  }
};

// Re-export token utilities for convenience
export { getCompanyIdFromToken, getUserIdFromToken } from "./tokenUtils";